import { Controller } from 'react-hook-form';
import { Alert, Box, Grid, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { CtaButton, SelectInput, TextInput } from '@tassomutuo-backoffice/ui';

import { CapAutocompleteInput } from '../../../../components';
import { createCdqLead } from '../../../../services/api/acquisition/cdqAcquisition';
import AcquisitionConfirmDialog from '../AcquisitionConfirmDialog';
import AcquisitionCustomerFields from '../AcquisitionCustomerFields';
import AcquisitionFormActions from '../AcquisitionFormActions';
import AcquisitionFormSection from '../AcquisitionFormSection';
import AcquisitionRecoveryPanel from '../AcquisitionRecoveryPanel';
import {
  acquisitionAmountInput,
  acquisitionAmountToNumber,
} from '../acquisitionAmount';
import { ACQUISITION_PRODUCT_LABEL } from '../acquisitionProducts';
import { AcquisitionActivityStore } from '../acquisitionActivity';
import { AcquisitionSessionStore } from '../acquisitionSessions';
import {
  acquisitionIncomeRule,
  acquisitionPensionRule,
  acquisitionRequestedAmountRule,
} from '../acquisitionValidation';
import { useAcquisitionPanel } from '../useAcquisitionPanel';
import { CDQ_INELIGIBILITY_MESSAGE, cdqIneligibility } from './cdqEligibility';
import { describeCdqAcquisitionFailure } from './cdqErrors';
import {
  CDQ_ASKS,
  CDQ_DEFAULT_VALUES,
  CdqConditionalField,
  CdqFormValues,
  buildCdqCommand,
  cdqInactiveFields,
} from './cdqForm';
import {
  CDQ_EMPLOYEES_NUMBER_OPTIONS,
  CDQ_OCCUPATION_OPTIONS,
  CDQ_SALARY_INSTALMENTS_OPTIONS,
  CDQ_TENURE_OPTIONS,
  CDQ_YES_NO_OPTIONS,
} from './cdqOptions';
import {
  CDQ_REVIEW_NOTICE,
  CDQ_REVIEW_TITLE,
  cdqReviewSections,
} from './cdqReview';
import { cdqAgeInput, cdqAgeRule, cdqAgeToNumber } from './cdqValidation';

const PRODUCT = 'cessioni-del-quinto' as const;

/** The answers the branching and the qualification policy read. */
const WATCHED_ANSWERS = [
  'occupazione',
  'anni',
  'redditoMensilePensionato',
  'indeterminato',
  'tempo',
  'nDipendenti',
] as const;

interface IProps {
  /** Where this product's unfinished draft and command identity outlive the tab. */
  sessions: AcquisitionSessionStore;
  /** What the workspace is told about this compilation while it is mounted. */
  activity: AcquisitionActivityStore;
}

/**
 * Assisted Cessione del quinto acquisition: one page, four sections, and one
 * authenticated command that Tasso Quinto owns end to end. The public funnel's
 * wrapping — consent, attribution, consultant and agency routing, tracking,
 * FIDO, autosave — is neither collected nor sent, and neither is
 * `approfondisci`: the backend owns that decision and imposes `false` itself,
 * so sending it at all would fail the closed schema.
 */
const CdqPanel = ({ sessions, activity }: IProps) => {
  const {
    control,
    errors,
    formRef,
    onSubmit,
    answers,
    blockingMessage,
    reviewedCommand,
    isPending,
    isBusy,
    isLocked,
    incompleteNotice,
    submissionError,
    recovery,
    confirmAcquisition,
    cancelReview,
  } = useAcquisitionPanel({
    product: PRODUCT,
    sessions,
    activity,
    defaultValues: CDQ_DEFAULT_VALUES,
    watch: [...WATCHED_ANSWERS],
    inactiveFields: cdqInactiveFields,
    ineligibility: (values) => {
      const reason = cdqIneligibility(values);
      return reason ? CDQ_INELIGIBILITY_MESSAGE[reason] : null;
    },
    buildCommand: buildCdqCommand,
    createLead: createCdqLead,
    describeFailure: describeCdqAcquisitionFailure,
  });

  const asks = (field: CdqConditionalField) => CDQ_ASKS[field](answers);

  return (
    <>
      <Typography color="text.secondary" mb={3}>
        Registra una lead cessione del quinto. La lead entra centralmente in
        Programma Tasso come Lead in gestione.
      </Typography>

      <AcquisitionRecoveryPanel
        productLabel={ACQUISITION_PRODUCT_LABEL[PRODUCT]}
        notice={incompleteNotice}
        recovery={recovery}
      />
      {submissionError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submissionError}
        </Alert>
      )}

      <Box component="form" ref={formRef} onSubmit={onSubmit} noValidate>
        <fieldset
          disabled={isBusy || isLocked}
          style={{ border: 0, padding: 0, margin: 0 }}
        >
          <AcquisitionFormSection
            title="Richiesta"
            description="Importo da finanziare con la cessione."
            icon={<RequestQuoteOutlinedIcon />}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="importo"
                  rules={{
                    required: "Inserisci l'importo richiesto",
                    validate: (value) =>
                      acquisitionRequestedAmountRule(
                        acquisitionAmountToNumber(value)
                      ),
                  }}
                  render={({ field }) => (
                    <TextInput
                      label="Importo richiesto"
                      showEuroIcon
                      error={!!errors.importo}
                      helperText={errors.importo?.message}
                      {...field}
                      onChange={(e: { target: { value: string } }) =>
                        field.onChange(acquisitionAmountInput(e.target.value))
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AcquisitionFormSection>

          <AcquisitionFormSection
            title="Situazione lavorativa e requisiti"
            description="Le risposte determinano le domande successive e l'idoneità del cliente."
            icon={<WorkOutlineIcon />}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="occupazione"
                  rules={{ required: 'Seleziona l’occupazione' }}
                  render={({ field }) => (
                    <SelectInput
                      label="Occupazione"
                      selectOptions={CDQ_OCCUPATION_OPTIONS}
                      error={!!errors.occupazione}
                      helperText={errors.occupazione?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="anni"
                  rules={{
                    required: "Inserisci l'età",
                    validate: (value) => cdqAgeRule(cdqAgeToNumber(value)),
                  }}
                  render={({ field }) => (
                    <TextInput
                      label="Età"
                      error={!!errors.anni}
                      helperText={errors.anni?.message}
                      {...field}
                      onChange={(e: { target: { value: string } }) =>
                        field.onChange(cdqAgeInput(e.target.value))
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="spid"
                  rules={{ required: 'Indica se il cliente ha lo SPID' }}
                  render={({ field }) => (
                    <SelectInput
                      label="SPID"
                      selectOptions={CDQ_YES_NO_OPTIONS}
                      error={!!errors.spid}
                      helperText={errors.spid?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="cittadinanzaItaliana"
                  rules={{ required: 'Indica la cittadinanza italiana' }}
                  render={({ field }) => (
                    <SelectInput
                      label="Cittadinanza italiana"
                      selectOptions={CDQ_YES_NO_OPTIONS}
                      error={!!errors.cittadinanzaItaliana}
                      helperText={errors.cittadinanzaItaliana?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="altriPrestitiInCorso"
                  rules={{
                    required: 'Indica se ci sono prestiti o cessioni in corso',
                  }}
                  render={({ field }) => (
                    <SelectInput
                      label="Prestiti o cessioni in corso"
                      selectOptions={CDQ_YES_NO_OPTIONS}
                      error={!!errors.altriPrestitiInCorso}
                      helperText={errors.altriPrestitiInCorso?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="ritardiPagamenti"
                  rules={{
                    required: 'Indica se ci sono stati ritardi nei pagamenti',
                  }}
                  render={({ field }) => (
                    <SelectInput
                      label="Ritardi nei pagamenti"
                      selectOptions={CDQ_YES_NO_OPTIONS}
                      error={!!errors.ritardiPagamenti}
                      helperText={errors.ritardiPagamenti?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              {asks('redditoMensilePensionato') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="redditoMensilePensionato"
                    defaultValue=""
                    rules={{
                      required: 'Inserisci la pensione mensile',
                      validate: (value) =>
                        acquisitionPensionRule(
                          acquisitionAmountToNumber(value)
                        ),
                    }}
                    render={({ field }) => (
                      <TextInput
                        label="Pensione mensile"
                        showEuroIcon
                        error={!!errors.redditoMensilePensionato}
                        helperText={errors.redditoMensilePensionato?.message}
                        {...field}
                        onChange={(e: { target: { value: string } }) =>
                          field.onChange(acquisitionAmountInput(e.target.value))
                        }
                      />
                    )}
                  />
                </Grid>
              )}
              {asks('stipendio') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="stipendio"
                    defaultValue=""
                    rules={{
                      required: 'Inserisci il reddito mensile',
                      validate: (value) =>
                        acquisitionIncomeRule(acquisitionAmountToNumber(value)),
                    }}
                    render={({ field }) => (
                      <TextInput
                        label="Reddito mensile"
                        showEuroIcon
                        error={!!errors.stipendio}
                        helperText={errors.stipendio?.message}
                        {...field}
                        onChange={(e: { target: { value: string } }) =>
                          field.onChange(acquisitionAmountInput(e.target.value))
                        }
                      />
                    )}
                  />
                </Grid>
              )}
              {asks('indeterminato') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="indeterminato"
                    defaultValue=""
                    rules={{
                      required:
                        'Indica se il contratto è a tempo indeterminato',
                    }}
                    render={({ field }) => (
                      <SelectInput
                        label="Contratto a tempo indeterminato"
                        selectOptions={CDQ_YES_NO_OPTIONS}
                        error={!!errors.indeterminato}
                        helperText={errors.indeterminato?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
              {asks('mensilita') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="mensilita"
                    defaultValue=""
                    rules={{ required: 'Seleziona il numero di mensilità' }}
                    render={({ field }) => (
                      <SelectInput
                        label="Mensilità"
                        selectOptions={CDQ_SALARY_INSTALMENTS_OPTIONS}
                        error={!!errors.mensilita}
                        helperText={errors.mensilita?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
              {asks('tempo') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="tempo"
                    defaultValue=""
                    rules={{
                      required: 'Seleziona gli anni nell’azienda attuale',
                    }}
                    render={({ field }) => (
                      <SelectInput
                        label="Anni nell’azienda attuale"
                        selectOptions={CDQ_TENURE_OPTIONS}
                        error={!!errors.tempo}
                        helperText={errors.tempo?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
              {asks('nDipendenti') && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    control={control}
                    name="nDipendenti"
                    defaultValue=""
                    rules={{ required: 'Seleziona il numero di dipendenti' }}
                    render={({ field }) => (
                      <SelectInput
                        label="Numero di dipendenti"
                        selectOptions={CDQ_EMPLOYEES_NUMBER_OPTIONS}
                        error={!!errors.nDipendenti}
                        helperText={errors.nDipendenti?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>

            {blockingMessage && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                {blockingMessage}
              </Alert>
            )}
          </AcquisitionFormSection>

          <AcquisitionFormSection
            title="Residenza"
            description="Comune di residenza del cliente."
            icon={<PlaceOutlinedIcon />}
          >
            <Box maxWidth={400}>
              <Controller
                control={control}
                name="location"
                rules={{
                  validate: (value) =>
                    !!value ||
                    "Seleziona una località dai risultati dell'autocomplete",
                }}
                render={({ field, fieldState }) => (
                  <CapAutocompleteInput
                    id="cdq-location"
                    label="Cerca per CAP o comune"
                    value={field.value}
                    onChange={(option) => field.onChange(option?.value ?? null)}
                    errorMessage={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </AcquisitionFormSection>

          <AcquisitionFormSection
            title="Cliente"
            description="Dati anagrafici e recapiti del cliente."
            icon={<PersonOutlineIcon />}
          >
            <AcquisitionCustomerFields<CdqFormValues>
              control={control}
              idPrefix="cdq"
            />
          </AcquisitionFormSection>

          <AcquisitionFormActions>
            <CtaButton
              variant="contained"
              type="submit"
              loading={isPending}
              disabled={isBusy || isLocked || !!blockingMessage}
            >
              Registra lead Cessione del quinto
            </CtaButton>
          </AcquisitionFormActions>
        </fieldset>
      </Box>

      {reviewedCommand && (
        <AcquisitionConfirmDialog
          open
          title={CDQ_REVIEW_TITLE}
          notice={CDQ_REVIEW_NOTICE}
          sections={cdqReviewSections(reviewedCommand)}
          pending={isPending}
          onEdit={cancelReview}
          onConfirm={confirmAcquisition}
        />
      )}
    </>
  );
};

export default CdqPanel;
